import { Injectable, inject, Injector } from '@angular/core';
import { BaseListenService, DB_CONNECTION_CONFIGS, PouchDB } from '@luzula/luzula-lib';
import type { ListConfig } from '@luzula/luzula-lib';
import { DatabaseConfigService } from '../database-config.service';

export type { ListMetadata, ListConfig, ListType } from '@luzula/luzula-lib';

export interface BodenartEntry {
  name: string;
  Lagerungsdichte: { gering: number; mittel: number; hoch: number };
  Humusgehalt: { h2: number; h3: number; h4: number; h5: number; h6: number };
}

export interface FeinbodenformEntry {
  FBF: string;
  FBF_display: string;
  name: string;
  HBF: string;
  HBF_lang?: string;
  NKr: string;
  Hw: string | null;
  zuordnung: Record<string, string>;
  Serie: string | null;
  Seite?: string;
}

// Standard-Listen aus JSON-Dateien
import Forstämter from '../../assets/json/Forstämter.json';
import Humusformen from '../../assets/json/Humusformen.json';
import HorizontSymbole from '../../assets/json/HorizontSymbole.json';
import Bodenarten from '../../assets/json/Bodenarten.json';
import Wuchsgebiete from '../../assets/json/Wuchsgebiete.json';
import ReliefformUmgebung from '../../assets/json/ReliefformUmgebung.json';
import Nährkraftstufen from '../../assets/json/Nährkraftstufen.json';
import Substrattypengruppen from '../../assets/json/Substrattypengruppen.json';
import BodenformSuffixe from '../../assets/json/BodenformSuffixe.json';
import Torfarten from '../../assets/json/Torfarten.json';
import Farben from '../../assets/json/Farben.json';
import Klimastufen from '../../assets/json/Klimastufen.json';
import Feinbodenformen from '../../assets/json/Feinbodenformen.json';
import BodenartenTabelle from '../../assets/json/BodenartenTabelle.json';

@Injectable({
  providedIn: 'root'
})
export class ListenService extends BaseListenService {
  private injector = inject(Injector);
  private dbConfig = inject(DatabaseConfigService);
  private dbConnectionConfigs = inject(DB_CONNECTION_CONFIGS);

  static bodenartenTabelleToArray(): BodenartEntry[] {
    return Object.entries(BodenartenTabelle).map(([name, data]) => ({
      name,
      Lagerungsdichte: (data as any).Lagerungsdichte,
      Humusgehalt: (data as any).Humusgehalt
    }));
  }

  static bodenartenTabelleFromArray(entries: BodenartEntry[]): Record<string, { Lagerungsdichte: any; Humusgehalt: any }> {
    const obj: Record<string, any> = {};
    for (const e of entries) {
      obj[e.name] = { Lagerungsdichte: e.Lagerungsdichte, Humusgehalt: e.Humusgehalt };
    }
    return obj;
  }

  readonly editableLists: ListConfig[] = [
    { name: 'Feinbodenformen', displayName: 'Feinbodenformen', type: 'feinbodenform', defaultData: Feinbodenformen as any[] },
    { name: 'Forstämter', displayName: 'Forstämter', type: 'simple', defaultData: Forstämter },
    { name: 'Humusformen', displayName: 'Humusformen', type: 'simple', defaultData: Humusformen },
    { name: 'HorizontSymbole', displayName: 'Horizont-Symbole', type: 'simple', defaultData: HorizontSymbole },
    { name: 'Bodenarten', displayName: 'Bodenarten', type: 'simple', defaultData: Bodenarten },
    { name: 'BodenartenTabelle', displayName: 'Bodenarten nWSK-Eigenschaften', type: 'bodenartenTabelle', defaultData: ListenService.bodenartenTabelleToArray() },
    { name: 'Wuchsgebiete', displayName: 'Wuchsgebiete', type: 'simple', defaultData: Wuchsgebiete },
    { name: 'ReliefformUmgebung', displayName: 'Reliefform Umgebung', type: 'simple', defaultData: ReliefformUmgebung },
    { name: 'Nährkraftstufen', displayName: 'Nährkraftstufen', type: 'simple', defaultData: Nährkraftstufen },
    { name: 'Substrattypengruppen', displayName: 'Substrattypengruppen', type: 'simple', defaultData: Substrattypengruppen },
    { name: 'BodenformSuffixe', displayName: 'Bodenform-Suffixe', type: 'valueNamePair', defaultData: BodenformSuffixe },
    { name: 'Torfarten', displayName: 'Torfarten', type: 'valueNamePair', defaultData: Torfarten },
    { name: 'Farben', displayName: 'Farben', type: 'valueNamePair', defaultData: Farben },
    { name: 'Klimastufen', displayName: 'Klimastufen', type: 'simple', defaultData: Klimastufen },
  ];

  private _currentUser: string | null = null;

  constructor() {
    super();
    this.dbReady = this.initRemoteDb();

    window.addEventListener('online', () => {
      console.log('ListenService: Online-Event erkannt');
      if (!this.remoteDb) {
        this.dbReady = this.initRemoteDb();
      }
    });

    window.addEventListener('offline', () => {
      console.log('ListenService: Offline-Event erkannt');
      this.remoteDb = null;
      this.storageType$.next('local');
    });
  }

  protected getRemoteDb(): any | null {
    return this.remoteDb;
  }

  protected getCurrentUser(): string {
    if (this._currentUser) return this._currentUser;
    return 'Unbekannt';
  }

  private async initRemoteDb(): Promise<void> {
    if (!navigator.onLine) {
      this.storageType$.next('local');
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 2000));

    const conn = this.dbConnectionConfigs[0];
    if (!conn) return;

    try {
      const db = new PouchDB(conn.url(this.dbConfig.punkteDbName), { skip_setup: true });
      const info = await db.info();
      if (!(info as any).error) {
        this.remoteDb = db;
        this.storageType$.next('database');
        console.log('ListenService: DB-Verbindung hergestellt');
        this.cacheListsForOffline();
        return;
      }
    } catch (e) {
      // Verbindung fehlgeschlagen
    }
    this.storageType$.next('local');
    console.warn('ListenService: Keine DB-Verbindung möglich');
  }

  /** Override saveList um AuthService lazy zu laden */
  override async saveList(listName: string, data: any[]): Promise<void> {
    // Sicherstellen dass currentUser geladen ist
    const { AuthService } = await import('../auth.service');
    const authService = this.injector.get(AuthService);
    this._currentUser = authService.currentUser || 'Unbekannt';
    return super.saveList(listName, data);
  }
}
